import axios, { AxiosResponse } from 'axios';

// Base URL for the API
const API_BASE_URL = process.env.REACT_APP_BACKEND_API_URL || 'https://communicationprodapi.earlyjobs.ai/api';

// Define enums for statuses
export enum InterviewOverallStatus {
  PENDING = 'PENDING',
  SCHEDULED = 'SCHEDULED',
  IN_PROGRESS = 'IN_PROGRESS',
  QUALIFIED = 'QUALIFIED',
  REJECTED = 'REJECTED'
}

export enum InterviewRoundStatus {
  PENDING_SCHEDULE = 'PENDING_SCHEDULE',
  SCHEDULED = 'SCHEDULED',
  RESCHEDULED = 'RESCHEDULED',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED'
}

export enum ApplicationStatus {
  NEW = 'NEW',
  UNDER_REVIEW = 'UNDER_REVIEW',
  INTERVIEW_SCHEDULED = 'INTERVIEW_SCHEDULED',
  INTERVIEW_RESCHEDULED = 'INTERVIEW_RESCHEDULED',
  INTERVIEW_COMPLETED = 'INTERVIEW_COMPLETED',
  OFFERED = 'OFFERED',
  SELECTED = 'SELECTED',
  REJECTED_BY_COMPANY = 'REJECTED_BY_COMPANY',
  REJECTED_BY_CANDIDATE = 'REJECTED_BY_CANDIDATE'
}

// Setup Axios interceptor for token refresh
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    // If 401 and not already retried
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        // Always send refresh token as cookie
        const res = await axios.post(
          `${API_BASE_URL}/auth/refresh-token`,
          {},
          {
            headers: { 'Content-Type': 'application/json' },
            withCredentials: true,
          }
        );
        const newAccessToken = res.data.accessToken;
        if (newAccessToken) {
          localStorage.setItem('accessToken', newAccessToken);
          originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
          return axios(originalRequest);
        } else {
          localStorage.removeItem('accessToken');
          if (typeof window !== 'undefined' && window.history && window.history.pushState) {
            window.history.pushState({}, '', '/login');
            window.dispatchEvent(new Event('popstate'));
          } else {
            window.location.href = '/login';
          }
        }
      } catch (refreshError) {
        localStorage.removeItem('accessToken');
        if (typeof window !== 'undefined' && window.history && window.history.pushState) {
          window.history.pushState({}, '', '/login');
          window.dispatchEvent(new Event('popstate'));
        } else {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// Interfaces for request and response payloads
export interface ScheduleInterviewPayload {
  interviewType: 'Online' | 'Offline';
  scheduledDate: string; // ISO 8601 Date string
  startTime: string;   // e.g., "14:30"
  endTime: string;     // e.g., "15:30"
  timeZone: string;    // e.g., "Asia/Kolkata"
  interviewers: string[]; // Array of User IDs for the interviewers
  additionalParticipants?: string[]; // For Online interviews
  location?: string; // For Offline interviews
  notes?: string; // Optional notes for the round
}

export interface RescheduleInterviewPayload {
  scheduledDate?: string;
  startTime?: string;
  endTime?: string;
  timeZone?: string;
  interviewers?: string[];
  additionalParticipants?: string[];
  location?: string;
  notes?: string;
  rescheduleReason: string;
  transferOwnership?: boolean;
  keepExistingOwner?: boolean;
}

export interface UpdateRoundStatusPayload {
  outcome: 'Qualified' | 'Rejected';
  feedback: {
    rating: number;
    recommendation: string;
    comments: string;
  };
  isFinalRound: boolean;
}

export interface InterviewRound {
  id: string;
  roundName: string;
  status: InterviewRoundStatus;
  interviewType: 'Online' | 'Offline';
  scheduledDate?: string;
  startTime?: string;
  endTime?: string;
  timeZone?: string;
  meetingLink?: string;
  location?: string;
  notes?: string;
  additionalParticipants?: string[];
  interviewers: Array<{ username: string; email: string }>;
  feedback: Array<{
    interviewer: { username: string; email: string };
    rating: number;
    recommendation: string;
    comments: string;
    submittedAt: string;
  }>;
  statusLogs: Array<{
    status: InterviewRoundStatus;
    updatedBy: { username: string; email: string };
    timestamp: string;
    notes: string;
  }>;
}

export interface InterviewDetails {
  id: string;
  overallStatus: InterviewOverallStatus;
  currentRoundName: string;
  currentRoundStatus: InterviewRoundStatus;
  nextActionBy: string;
  statusLogs: Array<{
    status: InterviewOverallStatus;
    updatedBy: { username: string; email: string };
    timestamp: string;
    notes: string;
  }>;
  rounds: InterviewRound[];
}

export interface InterviewDetailsResponse {
  status: 'success';
  data: {
    interview: InterviewDetails;
  };
}

interface ApiResponse<T> {
  status: 'success';
  message: string;
  data?: T;
}

// API client functions
export const scheduleInterviewRound = async (
  interviewId: string,
  roundId: string,
  payload: ScheduleInterviewPayload
): Promise<ApiResponse<void>> => {
  try {
    const response: AxiosResponse<ApiResponse<void>> = await axios.put(
      `${API_BASE_URL}/interviews/${interviewId}/rounds/${roundId}/schedule`,
      payload,
      {
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to schedule interview round');
  }
};

export const rescheduleInterviewRound = async (
  interviewId: string,
  roundId: string,
  payload: RescheduleInterviewPayload
): Promise<ApiResponse<void>> => {
  try {
      const authHeaders = getAuthHeaders();
    console.log("scheduleInterviewRound authHeaders:", authHeaders); // <-- Add this line
    const response: AxiosResponse<ApiResponse<void>> = await axios.put(
      `${API_BASE_URL}/interviews/${interviewId}/rounds/${roundId}/reschedule`,
      payload,
      {
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to reschedule interview round');
  }
};

export const updateInterviewRoundStatus = async (
  interviewId: string,
  roundId: string,
  payload: UpdateRoundStatusPayload
): Promise<ApiResponse<{
  interviewId: string;
  newInterviewStatus: InterviewOverallStatus;
  newApplicationStatus: ApplicationStatus;
}>> => {
  try {
    // Hardcode recommendation to "Hire"
    const modifiedPayload = {
      ...payload,
      feedback: {
        ...payload.feedback,
        recommendation: "Hire"
      }
    };
    const response: AxiosResponse<
      ApiResponse<{
        interviewId: string;
        newInterviewStatus: InterviewOverallStatus;
        newApplicationStatus: ApplicationStatus;
      }>
    > = await axios.patch(
      `${API_BASE_URL}/interviews/${interviewId}/rounds/${roundId}/status`,
      modifiedPayload,
      {
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to update interview round status');
  }
};

export const getInterviewDetailsByApplicationId = async (
  applicationId: string
): Promise<InterviewDetailsResponse> => {
  try {
    const response: AxiosResponse<InterviewDetailsResponse> = await axios.get(
      `${API_BASE_URL}/interviews/${applicationId}/interview`,
      {
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch interview details');
  }
};

const getAuthHeaders = () => {
  const token = localStorage.getItem("accessToken");
  return token ? { Authorization: `Bearer ${token}` } : {};
};